import { PasswordDTO } from '../interfaces/password-dto';
import { GRUPOS_DEF } from '../constants/grupos-def.const';
import { fatorPesoEscalonado } from './peso-escalonado.util';

export interface EscapeConfig {
  enabled: boolean;
  planoMin: number;
  particularMin: number;
}

export interface FilaConfig {
  order: string[];
  weights: { [key: string]: number };
  escape: EscapeConfig;
}

// Ligado por padrão; unidade desliga com escape:{enabled:false} na config.
export const ESCAPE_PADRAO: EscapeConfig = { enabled: true, planoMin: 60, particularMin: 75 };

const TOLERANCIA_ATRASO_MS = 15 * 60 * 1000;

export function ordemPadrao(): string[] {
  return GRUPOS_DEF.map(g => g.key);
}

export function parseFilaConfig(json: string | null | undefined): FilaConfig {
  const padrao = (): FilaConfig => ({ order: ordemPadrao(), weights: {}, escape: { ...ESCAPE_PADRAO } });
  if (!json) return padrao();
  try {
    const obj = JSON.parse(json);
    if (Array.isArray(obj)) return { order: obj, weights: {}, escape: { ...ESCAPE_PADRAO } };
    return {
      order:   Array.isArray(obj.order)                      ? obj.order   : ordemPadrao(),
      weights: obj.weights && typeof obj.weights === 'object' ? obj.weights : {},
      escape: {
        enabled:       obj.escape ? obj.escape.enabled === true : true,
        planoMin:      typeof obj.escape?.planoMin      === 'number' ? obj.escape.planoMin      : 60,
        particularMin: typeof obj.escape?.particularMin === 'number' ? obj.escape.particularMin : 75,
      },
    };
  } catch {
    return padrao();
  }
}

export function pesoGrupo(key: string, config: FilaConfig): number {
  if (config.weights[key] > 0) return config.weights[key];
  const idx = config.order.indexOf(key);
  return idx === -1 ? 0 : config.order.length - idx;
}

export function esperaMs(p: PasswordDTO, now: number): number {
  return now - new Date(p.passwordGenerationDate).getTime();
}

function atrasado(p: PasswordDTO): boolean {
  if (p.hasSchedule === 'N' || !p.scheduledTo) return false;
  return new Date(p.passwordGenerationDate).getTime()
       > new Date(p.scheduledTo).getTime() + TOLERANCIA_ATRASO_MS;
}

export function qualificaGrupo(p: PasswordDTO, key: string, now: number): boolean {
  const wait = esperaMs(p, now);
  switch (key) {
    case 'prioridade80':    return p.verifyLetter === 'PR';
    case 'prioridade60':    return p.verifyLetter === 'P';
    case 'prioridadeGeral': return p.priority && p.verifyLetter !== 'PR' && p.verifyLetter !== 'P';
    case 'medicoOcioso':    return p.medicoOcioso === true && p.hasSchedule !== 'N';
    case 'lab':             return p.verifyLetter === 'L';
    case 'tme20Particular': return p.planType === 'PRIVATE' && wait > 20 * 60 * 1000;
    case 'tme15Plano':      return p.planType !== 'PRIVATE' && wait > 15 * 60 * 1000;
    case 'pontual':         return p.hasSchedule !== 'N' && !!p.scheduledTo && !atrasado(p);
    case 'walkIn':          return p.hasSchedule === 'N';
    case 'atrasados':       return atrasado(p);
    default:                return false;
  }
}

export function calcScoreFila(p: PasswordDTO, config: FilaConfig, now: number): number {
  return config.order.reduce((sum, key) =>
    qualificaGrupo(p, key, now)
      ? sum + pesoGrupo(key, config) * fatorPesoEscalonado(key, p.planType, esperaMs(p, now))
      : sum, 0);
}

// União das três condições de preferencial do back (prioridade80, prioridade60, prioridadeGeral).
export function ehPreferencial(p: PasswordDTO): boolean {
  return p.verifyLetter === 'PR' || p.verifyLetter === 'P' || p.priority === true;
}

export function estourouEspera(p: PasswordDTO, config: FilaConfig, now: number): boolean {
  if (!config.escape.enabled) return false;
  if (ehPreferencial(p)) return false;
  const teto = p.planType === 'PRIVATE' ? config.escape.particularMin : config.escape.planoMin;
  return esperaMs(p, now) / 60000 > teto;
}

export function ordenarFilaExibicao(
  list: PasswordDTO[], config: FilaConfig, now: number,
): PasswordDTO[] {
  const nascimento = (p: PasswordDTO) => new Date(p.passwordGenerationDate).getTime();
  return [...list].sort((a, b) => {
    const ea = estourouEspera(a, config, now) ? 1 : 0;
    const eb = estourouEspera(b, config, now) ? 1 : 0;
    if (ea !== eb) return eb - ea;
    if (ea === 1) return nascimento(a) - nascimento(b);
    const scoreDiff = calcScoreFila(b, config, now) - calcScoreFila(a, config, now);
    if (scoreDiff !== 0) return scoreDiff;
    return nascimento(a) - nascimento(b);
  });
}
